import {AxiosError} from 'axios';

import {HTTP_STATUS_CODE} from '@zzng/common/constants/httpStatusCode';
import {isCbt, isDev, isReal} from '@zzng/common/utils/common';

import * as Sentry from '@sentry/browser';

export type SeverityLevel = 'fatal' | 'error' | 'warning' | 'log';

type SentryArgs = {
  dsn: string;
  origin: string;
  tags?: {[key: string]: string};
  enabled?: boolean;
};

export const initSentry = ({dsn, origin, tags, enabled = isCbt || isReal}: SentryArgs) => {
  if (enabled) {
    Sentry.init({dsn});
    Sentry.setTags({origin, ...tags});
  }
};

export const setSentryErrorLevel = (error: AxiosError) => {
  Sentry.configureScope((scope) => {
    const {name, message, code, status, response} = error;
    const context = {name, message, level: Sentry.Severity.Warning};
    const statusCode = status ?? response?.status;

    if (code === AxiosError.ERR_NETWORK) {
      Object.assign(context, {name: 'NetworkError', level: Sentry.Severity.Log});
    }

    if (code === AxiosError.ERR_CANCELED) {
      Object.assign(context, {name: 'CanceledError', level: Sentry.Severity.Log});
    }

    if (code === AxiosError.ECONNABORTED) {
      Object.assign(context, {name: 'AbortedRequest', level: Sentry.Severity.Log});
    }

    if (statusCode === HTTP_STATUS_CODE.UNAUTHORIZED) {
      Object.assign(context, {name: 'UnauthorizedError', level: Sentry.Severity.Log});
    }

    scope.setContext('ERROR Detail', context);
    scope.setLevel(context.level);
    Sentry.captureException(error);
  });
};

export const captureException = (message: string) => {
  if (isDev) {
    console.error(message);
    return;
  }

  Sentry.captureException(new Error(message));
};

export const captureExceptionWithLevel = (message: string, level: SeverityLevel) => {
  if (isDev) {
    console.warn(message);
    return;
  }

  Sentry.captureException(new Error(message), {level: level as Sentry.Severity});
};
